import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type WorkflowStepProps = {
  icon: LucideIcon;
  title: string;
  description?: ReactNode;
  /** A control shown beside the title, such as an on/off switch. */
  action?: ReactNode;
  /** Dims the step when an earlier step means it won't run. */
  inactive?: boolean;
  /** "outcome" marks the final step, where the workflow ends. */
  tone?: "default" | "outcome";
  isLast?: boolean;
  children?: ReactNode;
};

const iconTone = {
  default: "border-primary/40 bg-primary/5 text-primary",
  outcome: "border-success/40 bg-success/10 text-success",
};

const cardTone = {
  default: "bg-card",
  outcome: "border-success/30 bg-success/5",
};

/** One step of the top-to-bottom workflow, joined to the next step by a connector line. */
export const WorkflowStep = ({
  icon: Icon,
  title,
  description,
  action,
  inactive = false,
  tone = "default",
  isLast = false,
  children,
}: WorkflowStepProps) => (
  <li className="relative flex gap-4 pb-6 last:pb-0">
    {!isLast && (
      <span aria-hidden className="absolute top-10 bottom-0 left-5 w-px -translate-x-1/2 bg-border" />
    )}
    <span
      aria-hidden
      className={cn(
        "relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full border transition-colors",
        inactive ? "bg-background text-muted-foreground" : iconTone[tone],
      )}
    >
      <Icon className="size-5" />
    </span>
    <div className={cn("min-w-0 flex-1 rounded-lg border p-4 transition-opacity", cardTone[tone], inactive && "opacity-60")}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-base font-semibold leading-tight">{title}</h3>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {children && <div className="mt-4">{children}</div>}
    </div>
  </li>
);
